/**
 * AvatarPickerModal — gallery picker shown:
 *   • at signup (after the optional profile-picture upload step), and
 *   • from the Edit Profile page (anytime).
 *
 * Loads avatars from `/api/avatars` (optionally filtered by `gender`),
 * highlights the current selection, commits via `POST /api/users/me/avatar`.
 *
 * Style button (Gemini Nano Banana): after picking an avatar, the user
 * can tap "Style it" to send a short prompt (e.g. "change background to
 * sunset orange") which forks the avatar via /api/users/me/avatar/style
 * and saves the result.
 *
 * Admin variant uses a different `onConfirm` (passed in) so the same
 * picker can be mounted from the admin Manage tab.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Check, Sparkles, Shuffle, Wand2 } from 'lucide-react';
import { Button } from './ui/button';
import { cfResize } from '../utils/cfImage';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STYLE_SUGGESTIONS = [
  'change the background to a soft sunset orange',
  'change the background to a deep navy night sky with stars',
  'put them in a black hoodie',
  'change the background to a soft pastel pink',
  'add neon city lights behind them',
  'change the background to forest green',
];

export default function AvatarPickerModal({
  open,
  onClose,
  token,
  currentAvatarId = null,
  gender = null, // 'male' | 'female' | null — filters the gallery if set
  // Override the commit step (admin variant injects its own POST URL +
  // success callback). Default: write to the current user's profile.
  onConfirm = null,
  // Disable the "Style it" button (e.g. for the admin variant — they
  // shouldn't burn LLM credits styling on behalf of users).
  allowStyling = true,
  title = 'Choose your avatar',
  subtitle = "Pick one of our hand-illustrated portraits — you can change it anytime in Settings.",
  ctaLabel = 'Use this avatar',
}) {
  const [avatars, setAvatars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(currentAvatarId);
  const [submitting, setSubmitting] = useState(false);

  // Style-it state
  const [styling, setStyling] = useState(false);
  const [stylePrompt, setStylePrompt] = useState('');
  const [showStyle, setShowStyle] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const url = gender === 'male' || gender === 'female'
      ? `${API}/avatars?gender=${gender}`
      : `${API}/avatars`;
    axios.get(url)
      .then((r) => setAvatars(r.data?.avatars || []))
      .catch(() => toast.error("Couldn't load the avatar gallery"))
      .finally(() => setLoading(false));
  }, [open, gender]);

  useEffect(() => {
    setSelected(currentAvatarId);
  }, [currentAvatarId, open]);

  const randomize = () => {
    if (!avatars.length) return;
    const idx = Math.floor(Math.random() * avatars.length);
    setSelected(avatars[idx].id);
  };

  const handleConfirm = async () => {
    if (!selected) {
      toast.error('Pick one first');
      return;
    }
    setSubmitting(true);
    try {
      if (onConfirm) {
        await onConfirm(selected);
      } else {
        await axios.post(
          `${API}/users/me/avatar`,
          { avatar_id: selected },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        toast.success('Avatar updated');
      }
      onClose?.(selected);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save avatar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStyle = async () => {
    if (!selected) {
      toast.error('Pick an avatar to style first');
      return;
    }
    const prompt = stylePrompt.trim();
    if (prompt.length < 3) {
      toast.error('Describe a small change first');
      return;
    }
    if (!token) {
      toast.error('Log in to style your avatar');
      return;
    }
    setStyling(true);
    try {
      const r = await axios.post(
        `${API}/users/me/avatar/style`,
        { avatar_id: selected, prompt },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const newId = r.data?.chosen_avatar_id;
      if (newId) {
        toast.success('Your custom avatar is ready');
        onClose?.(newId);
      } else {
        toast.error('Styling failed — try a different prompt');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not style avatar');
    } finally {
      setStyling(false);
    }
  };

  const tiles = useMemo(() => avatars, [avatars]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
      onClick={onClose}
      data-testid="avatar-picker-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[90vh] flex flex-col bg-[#0a0a0a] border border-[#00CCFF]/30 shadow-[0_0_64px_rgba(0,204,255,0.12)] rounded-md overflow-hidden"
        data-testid="avatar-picker-modal"
      >
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[#1a1a1a] bg-gradient-to-r from-[#00CCFF]/8 via-transparent to-[#9b59b6]/8">
          <div>
            <div className="inline-flex items-center gap-2 text-[#00CCFF] font-mono text-[10px] uppercase tracking-[0.3em] mb-1">
              <Sparkles size={11} /> Avatar gallery
            </div>
            <h2 className="text-white font-['Chakra_Petch'] uppercase text-lg sm:text-xl tracking-wider">
              {title}
            </h2>
            <p className="text-[#888] font-mono text-xs mt-1 max-w-prose">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={() => onClose?.(null)}
            className="text-[#888] hover:text-white"
            aria-label="Close"
            data-testid="avatar-picker-close"
          >
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <p className="text-center text-[#888] font-mono text-sm py-12">Loading avatars…</p>
          ) : tiles.length === 0 ? (
            <p className="text-center text-[#888] font-mono text-sm py-12">
              No avatars available yet — the gallery is still being generated.
            </p>
          ) : (
            <div
              className="grid gap-3"
              style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))' }}
              data-testid="avatar-grid"
            >
              {tiles.map((a) => {
                const isSel = selected === a.id;
                // Picker tiles render at ~96-120 CSS px. Request a 256
                // variant from the CDN so the file is ~5-10× smaller
                // than the 1024 master.
                const baseSrc = a.cdn_url || `${process.env.REACT_APP_BACKEND_URL}${a.url}`;
                const src = cfResize(baseSrc, { w: 256, h: 256 });
                return (
                  <button
                    type="button"
                    key={a.id}
                    onClick={() => setSelected(a.id)}
                    className={`relative aspect-square overflow-hidden rounded-md transition-all ${
                      isSel
                        ? 'ring-2 ring-[#00CCFF] shadow-[0_0_18px_rgba(0,204,255,0.6)] scale-[1.03]'
                        : 'ring-1 ring-[#1a1a1a] hover:ring-[#00CCFF]/60'
                    }`}
                    data-testid={`avatar-tile-${a.id}`}
                    aria-pressed={isSel}
                    title={a.id}
                  >
                    <img
                      src={src}
                      alt={a.id}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover bg-[#111]"
                    />
                    {isSel && (
                      <span className="absolute top-1 right-1 bg-[#00CCFF] text-black w-5 h-5 rounded-full flex items-center justify-center">
                        <Check size={12} strokeWidth={3} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* "Style it" — Gemini Nano Banana personalisation. Only shown when
              a tile is selected AND the parent allows styling. */}
          {allowStyling && selected && !selected.startsWith('styled_') && (
            <div className="mt-5 p-4 bg-[#080808] border border-[#1a1a1a] rounded-sm">
              {!showStyle ? (
                <button
                  type="button"
                  onClick={() => setShowStyle(true)}
                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 border border-[#9b59b6]/40 text-[#9b59b6] hover:bg-[#9b59b6]/10 hover:border-[#9b59b6] rounded-none font-mono text-xs uppercase tracking-widest transition-colors"
                  data-testid="avatar-style-toggle"
                >
                  <Wand2 size={13} /> Style this avatar
                </button>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <Wand2 size={14} className="text-[#9b59b6]" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#9b59b6]">
                      Personalise via AI
                    </span>
                    <button
                      type="button"
                      onClick={() => { setShowStyle(false); setStylePrompt(''); }}
                      className="ml-auto text-[#666] hover:text-white text-xs font-mono"
                    >
                      hide
                    </button>
                  </div>
                  <input
                    type="text"
                    value={stylePrompt}
                    onChange={(e) => setStylePrompt(e.target.value.slice(0, 200))}
                    placeholder="e.g. change the background to sunset orange"
                    className="w-full bg-[#0a0a0a] border border-[#1a1a1a] focus:border-[#9b59b6] outline-none px-3 py-2 text-white font-mono text-sm transition-colors"
                    data-testid="avatar-style-prompt"
                    maxLength={200}
                  />
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {STYLE_SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setStylePrompt(s)}
                        className="px-2 py-1 bg-[#0a0a0a] border border-[#1a1a1a] hover:border-[#9b59b6]/60 text-[#888] hover:text-white font-mono text-[10px] transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  <Button
                    type="button"
                    onClick={handleStyle}
                    disabled={styling || stylePrompt.trim().length < 3}
                    className="rounded-none w-full mt-3 bg-[#9b59b6] text-white hover:bg-[#8e44ad] font-['Chakra_Petch'] uppercase tracking-wider disabled:opacity-50"
                    data-testid="avatar-style-submit"
                  >
                    <Wand2 size={13} className="mr-1.5" />
                    {styling ? 'Styling…' : 'Style my avatar'}
                  </Button>
                  <p className="text-[#555] font-mono text-[10px] mt-2 leading-relaxed">
                    Takes ~10s. We keep the same face & framing — only the
                    background / outfit / accent changes.
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        <footer className="px-5 py-4 bg-[#070707] border-t border-[#1a1a1a] flex flex-wrap items-center gap-3">
          <Button
            type="button"
            onClick={randomize}
            variant="outline"
            className="rounded-none border-[#1a1a1a] text-[#888] hover:text-white font-mono"
            data-testid="avatar-randomize-btn"
            disabled={!tiles.length || submitting || styling}
          >
            <Shuffle size={14} className="mr-1.5" /> Surprise me
          </Button>
          <span className="text-[#666] font-mono text-[10px] uppercase tracking-widest">
            {tiles.length} avatars
          </span>
          <Button
            type="button"
            onClick={() => onClose?.(null)}
            variant="outline"
            className="rounded-none border-[#1a1a1a] text-[#888] hover:text-white font-mono ml-auto"
            data-testid="avatar-cancel-btn"
            disabled={submitting || styling}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!selected || submitting || styling}
            className="rounded-none bg-[#00CCFF] text-black hover:bg-white font-['Chakra_Petch'] uppercase tracking-wider disabled:opacity-50"
            data-testid="avatar-confirm-btn"
          >
            <Check size={14} className="mr-1.5" /> {submitting ? 'Saving…' : ctaLabel}
          </Button>
        </footer>
      </div>
    </div>
  );
}
